import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Avatar, Badge, Button, Card, DatePicker, EmptyState, ErrorState, IconButton, Input, PageHeader, Skeleton, Tabs, useToast } from '../../../components/ui';
import { useAuth } from '../../../app/providers/AuthProvider';
import { useFetch } from '../../../hooks/useFetch';
import { cn } from '../../../utils/cn';
import { ATTENDANCE } from '../../../constants/status';
import { labelOf } from '../../../utils/labels';
import { PAY_TYPES } from '../../../constants/payments';
import { formatDate, parseISODate, toISODate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { attendanceService } from '../services/attendanceService';
import type { AttendanceStatus } from '../types/attendanceTypes';

interface MarkDraft {
  status: AttendanceStatus | null;
  overtimeHours: string | number;
  note: string;
}

const STATUS_KEYS = Object.keys(ATTENDANCE);
const TONE = {
  present: 'border-emerald-600 bg-emerald-600 text-white',
  half_day: 'border-sun-500 bg-sun-500 text-white',
  absent: 'border-red-600 bg-red-600 text-white',
  holiday: 'border-navy-500 bg-navy-500 text-white',
};
const CELL = { present: 'bg-emerald-50 text-emerald-700', half_day: 'bg-sun-50 text-sun-700', absent: 'bg-red-50 text-red-600', holiday: 'bg-navy-50 text-ink-500' };

const shiftDay = (iso: string, n: number) => {
  const d = parseISODate(iso) ?? new Date();
  d.setDate(d.getDate() + n);
  return toISODate(d);
};
const payText = (u) => (u.payType === 'daily' ? `${money(u.dailyWage)}/day` : u.payType === 'monthly' ? `${money(u.monthlySalary)}/month` : 'Not on payroll');

/** One date: mark everyone present / half day / absent / off, with overtime and a note. */
function Register({ date, setDate }: { date: string; setDate: (d: string) => void }) {
  const toast = useToast();
  const today = toISODate(new Date());
  const { data, error, reload } = useFetch(() => attendanceService.register(date), [date]);
  const [marks, setMarks] = useState<Record<string, MarkDraft>>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!data) return;
    setMarks(
      Object.fromEntries(
        data.rows.map(({ user, entry }) => [user._id, { status: entry?.status ?? null, overtimeHours: entry?.overtimeHours ? String(entry.overtimeHours) : '', note: entry?.note ?? '' }]),
      ),
    );
    setDirty(false);
  }, [data]);

  // People whose day is already paid on a salary slip can't be changed.
  const locked = useMemo(() => new Map((data?.rows ?? []).filter((r) => r.lockedBy).map((r) => [r.user._id, r.lockedBy])), [data]);

  const update = (id, patch) => {
    setMarks((m) => ({ ...m, [id]: { ...m[id], ...patch } }));
    setDirty(true);
  };
  const markAll = (status) => {
    setMarks((m) => Object.fromEntries(Object.entries(m).map(([id, v]) => [id, v.status || locked.has(id) ? v : { ...v, status }])));
    setDirty(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await attendanceService.save(
        date,
        Object.entries(marks)
          .filter(([userId]) => !locked.has(userId))
          .map(([userId, v]) => ({ userId, status: v.status, overtimeHours: Number(v.overtimeHours) || 0, note: v.note.trim() || undefined })),
      );
      toast.success(`Attendance saved for ${formatDate(date)}`);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const counts = useMemo(() => {
    const c = Object.fromEntries(STATUS_KEYS.map((k) => [k, 0]));
    let unmarked = 0;
    Object.values(marks).forEach((v) => (v.status ? (c[v.status] += 1) : (unmarked += 1)));
    return { ...c, unmarked };
  }, [marks]);

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-line p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-2">
          <IconButton icon="chevronLeft" label="Previous day" variant="outline" size="sm" onClick={() => setDate(shiftDay(date, -1))} />
          <div className="w-48">
            <DatePicker size="sm" aria-label="Date" value={date} onChange={(v) => v && setDate(v)} max={today} clearable={false} />
          </div>
          <IconButton icon="chevronRight" label="Next day" variant="outline" size="sm" disabled={date >= today} onClick={() => setDate(shiftDay(date, 1))} />
          {date !== today && <Button size="sm" variant="ghost" onClick={() => setDate(today)}>Today</Button>}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {STATUS_KEYS.map((k) => (
            <Badge key={k} tone={ATTENDANCE[k][1]}>{ATTENDANCE[k][0]} {counts[k]}</Badge>
          ))}
          {counts.unmarked > 0 && <Badge tone="grey">Not marked {counts.unmarked}</Badge>}
          <Button size="sm" variant="outline" leftIcon="check" onClick={() => markAll('present')} disabled={!counts.unmarked}>Mark rest present</Button>
        </div>
      </div>

      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : !data ? (
        <div className="space-y-2 p-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : data.rows.length === 0 ? (
        <EmptyState icon="users" title="No staff to mark" message="Add office staff and workers in Team. The owner is not included." />
      ) : (
        <ul className="divide-y divide-line">
          {data.rows.map(({ user }) => {
            const m = marks[user._id] ?? { status: null, overtimeHours: '', note: '' };
            const lock = locked.get(user._id);
            return (
              <li key={user._id} className="grid gap-3 px-4 py-3 lg:grid-cols-[minmax(0,1fr)_auto_90px_minmax(0,220px)] lg:items-center lg:px-5">
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar name={user.name} size={36} />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-navy-900">{user.name}</p>
                    <p className="truncate text-xs text-ink-400">{user.role === 'office' ? 'Office' : 'Worker'} · {payText(user)}</p>
                    {lock && (
                      <Link to={`/payroll/${lock._id}`} className="text-xs font-medium text-sun-700 hover:underline" title="This day is already paid; cancel the slip to change it">
                        Paid in {lock.slipNo}
                      </Link>
                    )}
                  </div>
                </div>
                <div className="flex gap-1" role="radiogroup" aria-label={`Attendance for ${user.name}`}>
                  {STATUS_KEYS.map((k) => (
                    <button
                      key={k}
                      type="button"
                      role="radio"
                      aria-checked={m.status === k}
                      disabled={!!lock}
                      onClick={() => update(user._id, { status: m.status === k ? null : k })}
                      className={cn(
                        'h-9 min-w-[64px] rounded-lg border px-2.5 text-[13px] font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-steel disabled:cursor-not-allowed disabled:opacity-60',
                        m.status === k ? TONE[k] : 'border-line bg-white text-ink-600 hover:border-navy-300',
                      )}
                    >
                      {ATTENDANCE[k][0]}
                    </button>
                  ))}
                </div>
                <Input
                  size="sm"
                  aria-label={`Overtime hours for ${user.name}`}
                  placeholder="OT hrs"
                  inputMode="decimal"
                  value={m.overtimeHours}
                  disabled={!!lock || !m.status || m.status === 'absent'}
                  onChange={(e) => update(user._id, { overtimeHours: e.target.value.replace(/[^\d.]/g, '') })}
                />
                <Input size="sm" aria-label={`Note for ${user.name}`} placeholder="Note / site" value={m.note} disabled={!!lock} onChange={(e) => update(user._id, { note: e.target.value })} maxLength={300} />
              </li>
            );
          })}
        </ul>
      )}

      {(data?.rows.length ?? 0) > 0 && (
        <div className={cn('sticky bottom-16 flex items-center justify-between gap-3 border-t border-line bg-white px-4 py-3 lg:bottom-0 lg:px-5', dirty && 'bg-brand-50/60')}>
          <span className="text-sm text-ink-500">{dirty ? 'You have unsaved changes' : `Saved for ${formatDate(date)}`}</span>
          <Button leftIcon="check" loading={saving} disabled={!dirty} onClick={save}>Save attendance</Button>
        </div>
      )}
    </Card>
  );
}

/** Month grid with totals per person. Clicking a day opens it in the register. */
function Sheet({ month, setMonth, openDay }: { month: string; setMonth: (m: string) => void; openDay: (d: string) => void }) {
  const { data, error, reload } = useFetch(() => attendanceService.sheet(month), [month]);
  const [y, m] = month.split('-').map(Number);
  const shiftMonth = (n: number) => {
    const d = new Date(y, m - 1 + n, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const thisMonth = toISODate(new Date()).slice(0, 7);
  const days = data ? Array.from({ length: data.days }, (_, i) => i + 1) : [];
  const weekday = (d: number) => new Date(y, m - 1, d).toLocaleDateString('en-IN', { weekday: 'narrow' });
  const isSunday = (d: number) => new Date(y, m - 1, d).getDay() === 0;

  const exportCsv = () => {
    if (!data) return;
    const head = ['Name', ...days.map(String), 'Present', 'Half day', 'Absent', 'Off', 'OT hours'];
    const lines = data.users.map((u) => {
      const e = data.entries[u._id] ?? {};
      const t = data.totals[u._id];
      return [u.name, ...days.map((d) => (e[d] ? ATTENDANCE[e[d].status][2] : '')), t.present, t.halfDay, t.absent, t.holiday, t.overtimeHours];
    });
    const csv = [head, ...lines].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `attendance-${month}.csv`;
    a.click();
  };

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line p-4">
        <div className="flex items-center gap-2">
          <IconButton icon="chevronLeft" label="Previous month" variant="outline" size="sm" onClick={() => shiftMonth(-1)} />
          <span className="w-36 text-center text-sm font-semibold text-navy-900">{new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</span>
          <IconButton icon="chevronRight" label="Next month" variant="outline" size="sm" disabled={month >= thisMonth} onClick={() => shiftMonth(1)} />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {STATUS_KEYS.map((k) => (
            <span key={k} className={cn('rounded px-1.5 py-0.5 font-semibold', CELL[k])}>{ATTENDANCE[k][2]} = {ATTENDANCE[k][0]}</span>
          ))}
          <Button size="sm" variant="outline" leftIcon="download" onClick={exportCsv} disabled={!data}>CSV</Button>
        </div>
      </div>
      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : !data ? (
        <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10" />)}</div>
      ) : data.users.length === 0 ? (
        <EmptyState icon="users" title="No staff yet" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-center text-xs">
            <thead className="text-ink-500">
              <tr className="border-b border-line">
                <th scope="col" className="sticky left-0 z-10 min-w-[160px] bg-white px-4 py-2 text-left text-[13px] font-medium">Name</th>
                {days.map((d) => (
                  <th key={d} scope="col" className={cn('min-w-[28px] px-0.5 py-1.5 font-normal', isSunday(d) && 'text-red-500')}>
                    <span className="block text-[10px]">{weekday(d)}</span>
                    {d}
                  </th>
                ))}
                {['P', 'H', 'A', 'O', 'OT'].map((h) => (
                  <th key={h} scope="col" className="min-w-[36px] bg-navy-50/60 px-1 py-2 font-semibold text-navy-700">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {data.users.map((u) => {
                const e = data.entries[u._id] ?? {};
                const t = data.totals[u._id];
                return (
                  <tr key={u._id}>
                    <th scope="row" className="sticky left-0 z-10 bg-white px-4 py-2 text-left text-[13px] font-medium text-navy-900">
                      {u.name}
                      <span className="block text-[11px] font-normal text-ink-400">{labelOf(PAY_TYPES, u.payType) || 'Not on payroll'}</span>
                    </th>
                    {days.map((d) => {
                      const mark = e[d];
                      const future = `${month}-${String(d).padStart(2, '0')}` > toISODate(new Date());
                      return (
                        <td key={d} className="p-0.5">
                          <button
                            type="button"
                            disabled={future}
                            onClick={() => openDay(`${month}-${String(d).padStart(2, '0')}`)}
                            title={mark ? `${ATTENDANCE[mark.status][0]}${mark.overtimeHours ? ` + ${mark.overtimeHours}h OT` : ''}` : 'Not marked'}
                            className={cn(
                              'h-7 w-full rounded text-[11px] font-semibold outline-none focus-visible:ring-2 focus-visible:ring-steel disabled:cursor-default',
                              mark ? CELL[mark.status] : 'text-ink-300 hover:bg-navy-50',
                            )}
                          >
                            {mark ? ATTENDANCE[mark.status][2] : future ? '' : '·'}
                            {mark?.overtimeHours ? <sup className="text-[8px]">+</sup> : null}
                          </button>
                        </td>
                      );
                    })}
                    <td className="tabular bg-navy-50/40 font-semibold text-emerald-700">{t.present}</td>
                    <td className="tabular bg-navy-50/40 font-semibold text-sun-700">{t.halfDay}</td>
                    <td className="tabular bg-navy-50/40 font-semibold text-red-600">{t.absent}</td>
                    <td className="tabular bg-navy-50/40 text-ink-500">{t.holiday}</td>
                    <td className="tabular bg-navy-50/40 text-ink-600">{t.overtimeHours || ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default function Attendance() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'sheet' ? 'sheet' : 'register';
  const date = params.get('date') || toISODate(new Date());
  const month = params.get('month') || date.slice(0, 7);
  const setQuery = (patch) => setParams({ view, date, month, ...patch }, { replace: true });

  return (
    <>
      <PageHeader
        title="Attendance"
        subtitle="Mark who worked each day. Salary slips use these marks."
        actions={user?.role === 'owner' && <Link to="/payroll/new"><Button leftIcon="rupee">Make salary slip</Button></Link>}
      />
      <Tabs
        className="mb-5"
        label="Attendance view"
        value={view}
        onChange={(v) => setQuery({ view: v })}
        tabs={[
          { value: 'register', label: 'Daily register' },
          { value: 'sheet', label: 'Monthly sheet' },
        ]}
      />
      {view === 'register' ? (
        <Register date={date} setDate={(d) => setQuery({ date: d, month: d.slice(0, 7) })} />
      ) : (
        <Sheet month={month} setMonth={(mo) => setQuery({ month: mo })} openDay={(d) => setQuery({ view: 'register', date: d, month: d.slice(0, 7) })} />
      )}
    </>
  );
}
